/**
 * Vuetify config
 *
 *  @author: Williams Francis <w.francis@nationaldrones.com> | <olawalewilliams9438@gmail.com>
 */

import { h } from 'vue';
import { customIcons } from 'js/customIcons';
import '@mdi/font/css/materialdesignicons.css';
import { aliases as mdiAliases } from 'vuetify/iconsets/mdi';
import { createVuetify } from 'vuetify';
import * as vuetifyComponents from 'vuetify/components';
import * as vuetifyDirectives from 'vuetify/directives';
import 'vuetify/styles';

const vuetifyThemeOptions = {
    themes: {
        light: {
            dark: false,
            colors: {
                primary: '#338eca',
                secondary: '#ff5722',
                accent: '#2196f3',
                background: '#f7f9fe',
                surface: '#ffffff',
                topBarBg: '#fafafa'
            }
        },
        dark: {
            dark: true,
            colors: {
                primary: '#338eca',
                secondary: '#ff5722',
                accent: '#2196f3',
                background: '#111827',
                surface: '#182231',
                topBarBg: '#131e2e'
            }
        }
    }
};

function getInitialVuetifyThemeName() {
    try {
        const savedTheme = localStorage.getItem('darkTheme');
        if (savedTheme === 'true' || savedTheme === 'false') {
            return savedTheme === 'true' ? 'dark' : 'light';
        }
    } catch (error) {
        // Default below if storage is blocked.
    }

    return typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
}

export const vuetifyIconAliases = {
    ...mdiAliases
};

const legacyVuetifyAliases = Object.keys(customIcons).reduce(
    (aliases, key) => ({
        ...aliases,
        [`vuetify.icons.${key}`]: key
    }),
    {}
);

const allIconAliases = {
    ...vuetifyIconAliases,
    ...legacyVuetifyAliases
};

function renderCustomIcon(iconName, props) {
    const iconDef = customIcons[iconName];
    if (!iconDef || !iconDef.component) {
        return null;
    }
    return h(iconDef.component, {
        ...(iconDef.props || {}),
        class: props.class
    });
}

const ndsdIconSet = {
    component: props => {
        let icon = props.icon;

        if (typeof icon === 'string') {
            if (icon.startsWith('$')) {
                icon = icon.slice(1);
            }
            if (icon.startsWith('vuetify.icons.')) {
                icon = icon.replace('vuetify.icons.', '');
            }
            if (allIconAliases[icon]) {
                icon = allIconAliases[icon];
            }
            const customIcon = renderCustomIcon(icon, props);
            if (customIcon) {
                return customIcon;
            }
        }

        if (
            icon &&
            typeof icon === 'object' &&
            icon.component &&
            icon.props
        ) {
            return h(icon.component, {
                ...icon.props,
                class: props.class
            });
        }

        if (typeof icon === 'string' && icon.startsWith('mdi-')) {
            return h('i', {
                class: ['mdi', icon, props.class]
            });
        }

        return h('i', { class: props.class });
    }
};

export default createVuetify({
    components: vuetifyComponents,
    directives: vuetifyDirectives,
    theme: {
        defaultTheme: getInitialVuetifyThemeName(),
        themes: vuetifyThemeOptions.themes
    },
    icons: {
        defaultSet: 'ndsd',
        aliases: allIconAliases,
        sets: {
            ndsd: ndsdIconSet
        }
    }
});
